// =============================================================================
// useApps Hook
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import { useLogCollectorClient } from './useLogCollectorClient.js';
import { ApiClientError, type App } from '../lib/api.js';

// =============================================================================
// Types
// =============================================================================

export interface UseAppsOptions {
  /** Base URL override */
  baseUrl?: string;
  /** Whether to fetch apps on mount (default: true) */
  fetchOnMount?: boolean;
}

export interface UseAppsResult {
  /** List of apps */
  apps: App[];
  /** Whether apps are being loaded */
  isLoading: boolean;
  /** Error from the last operation */
  error: string | null;
  /** Fetch/refresh apps list */
  fetchApps: () => Promise<void>;
  /** Create a new app */
  createApp: (name: string) => Promise<App | null>;
  /** Delete an app */
  deleteApp: (appId: string) => Promise<boolean>;
  /** Clear error state */
  clearError: () => void;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for managing apps.
 *
 * @example
 * ```tsx
 * function AppsList() {
 *   const { apps, isLoading, error, createApp, deleteApp, fetchApps } = useApps();
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error} />;
 *
 *   const handleCreate = async () => {
 *     const app = await createApp('My New App');
 *     if (app) {
 *       console.log('Created app:', app.id);
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleCreate}>Create App</button>
 *       <button onClick={fetchApps}>Refresh</button>
 *       <ul>
 *         {apps.map(app => (
 *           <li key={app.id}>
 *             {app.name}
 *             <button onClick={() => deleteApp(app.id)}>Delete</button>
 *           </li>
 *         ))}
 *       </ul>
 *     </div>
 *   );
 * }
 * ```
 */
export function useApps(options: UseAppsOptions = {}): UseAppsResult {
  const { baseUrl, fetchOnMount = true } = options;
  const { client } = useLogCollectorClient({ baseUrl });

  const [apps, setApps] = useState<App[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch apps list.
   */
  const fetchApps = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await client.listApps();
      setApps(result);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to fetch apps');
      }
    } finally {
      setIsLoading(false);
    }
  }, [client]);

  /**
   * Create a new app.
   */
  const createApp = useCallback(
    async (name: string): Promise<App | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const app = await client.createApp(name);
        setApps((prev) => [app, ...prev]);
        return app;
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to create app');
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [client]
  );

  /**
   * Delete an app.
   */
  const deleteApp = useCallback(
    async (appId: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        await client.deleteApp(appId);
        setApps((prev) => prev.filter((app) => app.id !== appId));
        return true;
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to delete app');
        }
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [client]
  );

  /**
   * Clear error state.
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Fetch on mount if enabled
  useEffect(() => {
    if (fetchOnMount) {
      fetchApps();
    }
  }, [fetchOnMount, fetchApps]);

  return {
    apps,
    isLoading,
    error,
    fetchApps,
    createApp,
    deleteApp,
    clearError,
  };
}
